import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { sdk } from "@/api/sdk"
import { type LoanDashboardStatus } from "@/generated/graphql"
import { queryClient } from "./client"

export const loanDashboardKeys = {
  all: ["loan", "loanDashboard", "loansCount"] as const,
  lists: () => [...loanDashboardKeys.all, "list"] as const,
  list: (filters: string) =>
    [...loanDashboardKeys.lists(), { filters }] as const,
  details: () => [...loanDashboardKeys.all, "detail"] as const,
  detail: (id: string) => [...loanDashboardKeys.details(), id] as const,
}

export const loanDashboardQueryOptions = (loanId?: string) => {
  return queryOptions({
    queryKey: loanDashboardKeys.detail(loanId || ""),
    enabled: !!loanId,
    // Set a more aggressive stale time to ensure the data is always fresh
    staleTime: 0,
    // Make sure loan data stays up to date
    refetchInterval: 1000 * 60, // 1 minute
    queryFn: async () => {
      if (!loanId) {
        return null
      }

      const result = await sdk.LoanDashboardQuery({ loanId })
      // The use of __typename here is for versioning purposes.
      // More types can be added in the future that aren't backwards compatible.
      if (
        result.dashboard?.getApplication.__typename === "BFLoanApplicationType"
      ) {
        return result.dashboard?.getApplication
      }
      return null
    },
  })
}

export const useLoanApplicationsCount = () => {
  return useQuery({
    queryKey: ["loansCount"],
    queryFn: async () => {
      try {
        const data = await sdk.LoanApplicationsCountQuery()
        return data.loanApplicationsCount
      } catch (err) {
        console.error(err)
      }
    },
  })
}

export type LoanDashboardDetailItem = NonNullable<
  NonNullable<
    NonNullable<
      Awaited<ReturnType<typeof sdk.LoanDashboardQuery>>["dashboard"]
    >["getApplication"]
  >
>

export const loanDashboardListInfiniteQueryOptions = (
  userStatus: LoanDashboardStatus
) => {
  return infiniteQueryOptions({
    queryKey: loanDashboardKeys.list(userStatus),
    initialPageParam: undefined,
    staleTime: 5000,
    refetchInterval: 1000 * 60, // 1 minute
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const result = await sdk.LoanDashboardListQuery({
        userStatus,
        first: 20,
        after: pageParam || undefined,
      })
      // The use of __typename here is for versioning purposes.
      // More types can be added in the future that aren't backwards compatible.
      if (
        result.loanQueries?.getApplications.__typename ===
        "BFLoanApplicationTypeConnection"
      ) {
        return result.loanQueries?.getApplications
      }
      return null
    },
    getNextPageParam: (lastPage) =>
      lastPage?.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : null,
    select: (data) => {
      if (!data) {
        return []
      }
      const nodes = data.pages
        .flatMap((page) => {
          return page?.edges.map((edge) => edge?.node)
        })
        .filter((node) => !!node)
      return nodes
    },
  })
}

export type LoanListQueryItem = NonNullable<
  NonNullable<
    NonNullable<
      Awaited<ReturnType<typeof sdk.LoanDashboardListQuery>>["loanQueries"]
    >["getApplications"]["edges"][0]
  >["node"]
>

export const useArchiveLoanMutation = () => {
  return useMutation({
    mutationFn: async ({ loanId }: { loanId: string }) => {
      return await sdk.archiveLoanApplication({ applicationId: loanId })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: loanDashboardKeys.lists(),
      })
    },
  })
}

export const useSkipLoanProcessorKickoffCall = () => {
  return useMutation({
    mutationFn: async ({ loanId }: { loanId: string }) => {
      return await sdk.setProcessingWelcomeCallMutation({
        loanId,
        processingWelcomeCall: "Opted out",
      })
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: loanDashboardKeys.detail(variables.loanId),
      })
    },
  })
}

export const useUnArchiveApplicationMutation = () => {
  return useMutation({
    mutationFn: async ({ loanId }: { loanId: string }) => {
      return await sdk.UnArchiveLoanApplication({ applicationId: loanId })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: loanDashboardKeys.lists(),
      })
    },
  })
}
